import type { Container } from 'pixi.js';
import type { Popup } from 'popups/Popup';
import type { EasingMap } from '../libs/TweenManager/easing';

export interface PopupElementAnimationData {
  container: Container;
  delay?: number;
  duration?: number;
  scale?: { fromX?: number; toX?: number; fromY?: number; toY?: number };
  scaleEase?: keyof typeof EasingMap;
  alpha?: number;
  customAnimator?: (animationData: PopupElementAnimationData, elementDelay: number) => Promise<void>;
  onInComplete?: () => void;
}

export async function animateInPopup(popup: Popup): Promise<void> {
  killPopupAnimations(popup);

  const { root } = popup;
  const elements = popup.getAnimatedElements?.('in') ?? [];

  if (elements.length > 0) {
    const animationPromises: Promise<void>[] = [];
    let elementDelay = 0;
    for (let i = 0; i < elements.length; i += 1) {
      const animationData = elements[i];
      const { container, delay = 0, customAnimator } = animationData;

      elementDelay += delay;
      const containerInteractive = container.interactive;
      container.interactive = false;

      // Run custom animator / alternative animation.
      if (customAnimator) {
        const customAnimationPromise = customAnimator(animationData, elementDelay).then((): void => {
          container.interactive = containerInteractive;
        });
        animationPromises.push(customAnimationPromise);
        continue;
      }

      const { duration = 1000, scale, scaleEase = 'elasticOut', alpha = 1, onInComplete } = animationData;

      container.props({
        alpha: 0,
        scaleX: scale?.fromX ?? 0,
        scaleY: scale?.fromY ?? 0,
      });

      if (!root.parent) return;

      // Tween alpha
      const alphaTween = root.parent.tweens
        .create(container)
        .to({ alpha }, duration / 3, 'quadOut', true, elementDelay);

      alphaTween.onComplete.addOnce(() => {
        container.interactive = containerInteractive;
      });

      animationPromises.push(alphaTween.asyncOnComplete());

      // Tween scale
      const scaleTween = root.parent.tweens
        .create(container)
        .to({ scaleX: scale?.toX ?? 1, scaleY: scale?.toY ?? 1 }, duration, scaleEase, true, elementDelay);

      animationPromises.push(scaleTween.asyncOnComplete());

      if (onInComplete)
        scaleTween.onComplete.addOnce(() => {
          onInComplete();
        });
    }

    await Promise.all(animationPromises);
  } else {
    // Basic fade in as fallback.
    await root.parent?.tweens.create(root).to({ alpha: 1 }, 500, 'quadOut', true).asyncOnComplete();
  }

  popup.onAnimationInComplete?.();
}

export async function animateOutPopup(popup: Popup, onAboutToComplete: () => void): Promise<void> {
  killPopupAnimations(popup);

  const { root } = popup;
  const elements = (popup.getAnimatedElements?.('out') ?? []).reverse();

  if (elements.length > 0) {
    const animationPromises: Promise<void>[] = [];
    let elementDelay = 0;
    for (let i = 0; i < elements.length; i += 1) {
      const { container, delay = 150, duration = 333, scaleEase = 'backIn' } = elements[i];

      if (!root.parent) return;

      // Tween alpha
      animationPromises.push(
        root.parent.tweens
          .create(container)
          .to({ alpha: 0 }, duration / 3, 'quadOut', true, elementDelay + (duration - delay / 3))
          .asyncOnComplete(),
      );

      // Tween scale
      const scaleTweenPromise = root.parent.tweens
        .create(container)
        .to({ scaleX: container.scaleX * 0.5, scaleY: container.scaleY * 0.5 }, duration, scaleEase, true, elementDelay)
        .asyncOnComplete();

      if (i === elements.length - 1) {
        scaleTweenPromise.then(onAboutToComplete).catch((e) => {
          console.error(e);
        });
      }
      animationPromises.push(scaleTweenPromise);

      elementDelay += delay;
    }
    await Promise.all(animationPromises);
  } else {
    // Basic fade out as fallback.
    onAboutToComplete();
    await root.parent?.tweens.create(root).to({ alpha: 0 }, 500, 'quadOut', true).asyncOnComplete();
  }
}

export function killPopupAnimations(popup: Popup): void {
  const { root } = popup;
  root.removeTweens(true);
}
